import { SidebarButton } from 'src/components/atoms/buttons/sidebar-button/sidebar-button';
import { PiSignOutLight, PiXLight } from 'react-icons/pi';
import { useAuth0 } from '@auth0/auth0-react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { NotificationsPopover } from 'src/modules/notifications/components/organisms/notifications-popover';
import { SidebarGroups } from './sidebar-groups';

import { SupportButton } from 'src/modules/support/components/molecules/support-button';
import { useSupport } from 'src/modules/support/hooks/provider-bounded/use-support';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileSidebar = ({ isOpen, onClose }: MobileSidebarProps) => {
  const { t } = useI18n();
  const { logout } = useAuth0();
  const { shutdown } = useSupport();

  const handleLogout = () => {
    shutdown();
    void logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    onClose();
  };

  const overlayClasses = `
    fixed inset-0 z-50 flex transition-opacity duration-300 ease-in-out
    ${isOpen ? 'opacity-100 pointer-events-auto' : 'invisible opacity-0 pointer-events-none'}
  `;

  const backdropClasses = `
    fixed inset-0 bg-black transition-opacity duration-300 ease-in-out
    ${isOpen ? 'opacity-50' : 'opacity-0'}
  `;

  const sidebarClasses = `
    relative flex flex-col bg-white h-full w-[280px] py-4 px-1
    text-primary-500 overflow-hidden
    transform transition-transform duration-300 ease-in-out
    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
  `;

  const headerButtonClasses = `
    border-primary-500 border !rounded-full !p-1.5
    hover:!bg-neutral-100 active:!bg-neutral-100
    focus:!ring-0 focus:!ring-offset-0 active:!ring-0 active:!ring-offset-0
  `;

  return (
    <div className={overlayClasses}>
      <div className={backdropClasses} onClick={onClose} aria-hidden="true" />
      <div className={sidebarClasses}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mx-3 my-[0.55rem] flex-none">
            <NotificationsPopover
              buttonClassName={headerButtonClasses}
              placement="bottom-start"
            />
            <Button
              onClick={onClose}
              title={t('sidebar.closeNavigation')}
              className={`
                ${headerButtonClasses} bg-neutral-100
              `}
              appearance="soft"
              variant="primary"
              size="sm"
              isIconOnly
            >
              <PiXLight className="h-5 w-5 text-primary-700" />
            </Button>
          </div>

          <nav className="space-y-4 flex-1 overflow-y-auto">
            <SidebarGroups variant="mobile" onItemClick={onClose} />
          </nav>

          <div className="py-4 space-y-4 border-t border-neutral-200 bg-white flex-none">
            <div className="px-3">
              <SupportButton onOpen={onClose} />
            </div>
            <SidebarButton
              icon={<PiSignOutLight className="h-6 w-6" />}
              label={t('sidebar.logout')}
              onClick={handleLogout}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
